import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  X, TrendingUp, TrendingDown, Target, AlertTriangle,
  DollarSign, Info, ChevronDown, ChevronUp
} from 'lucide-react';

// Calculate option payoff at expiration
const calculateOptionPayoff = (stockPrice, strike, optionType, premium, quantity, contractSize = 100) => {
  const isCall = optionType?.toLowerCase() === 'call';
  const isPut = optionType?.toLowerCase() === 'put';
  const isLong = quantity > 0;
  const contracts = Math.abs(quantity);
  
  let intrinsicValue = 0;
  if (isCall) {
    intrinsicValue = Math.max(0, stockPrice - strike);
  } else if (isPut) {
    intrinsicValue = Math.max(0, strike - stockPrice);
  }
  
  // P&L calculation
  const premiumPaid = premium * contractSize * contracts;
  const valueAtExpiry = intrinsicValue * contractSize * contracts;
  
  if (isLong) {
    return valueAtExpiry - premiumPaid;
  } else {
    // Short position
    return premiumPaid - valueAtExpiry;
  }
};

// Calculate spread payoff (for multi-leg strategies)
const calculateSpreadPayoff = (stockPrice, legs) => {
  let totalPayoff = 0;
  
  legs.forEach(leg => {
    const payoff = calculateOptionPayoff(
      stockPrice,
      leg.strike_price || leg.strike,
      leg.option_type,
      leg.entry_premium || leg.premium || 0,
      leg.quantity,
      100
    );
    totalPayoff += payoff;
  });
  
  return totalPayoff;
};

// Find breakeven points
const findBreakevenPoints = (legs, minPrice, maxPrice, steps = 1000) => {
  const breakevens = [];
  const priceStep = (maxPrice - minPrice) / steps;
  
  let prevPayoff = calculateSpreadPayoff(minPrice, legs);
  
  for (let i = 1; i <= steps; i++) {
    const price = minPrice + (i * priceStep);
    const currentPayoff = calculateSpreadPayoff(price, legs);
    
    // Check if we crossed zero
    if ((prevPayoff < 0 && currentPayoff >= 0) || (prevPayoff >= 0 && currentPayoff < 0)) {
      // Linear interpolation to find exact breakeven
      const ratio = Math.abs(prevPayoff) / (Math.abs(prevPayoff) + Math.abs(currentPayoff));
      const breakeven = (price - priceStep) + (priceStep * ratio);
      breakevens.push(breakeven);
    }
    
    prevPayoff = currentPayoff;
  }
  
  return breakevens;
};

// Calculate max profit and max loss
const findMaxProfitLoss = (legs, minPrice, maxPrice, steps = 1000) => {
  let maxProfit = -Infinity;
  let maxLoss = Infinity;
  let maxProfitPrice = minPrice;
  let maxLossPrice = minPrice;
  
  const priceStep = (maxPrice - minPrice) / steps;
  
  for (let i = 0; i <= steps; i++) {
    const price = minPrice + (i * priceStep);
    const payoff = calculateSpreadPayoff(price, legs);
    
    if (payoff > maxProfit) {
      maxProfit = payoff;
      maxProfitPrice = price;
    }
    if (payoff < maxLoss) {
      maxLoss = payoff;
      maxLossPrice = price;
    }
  }
  
  // Check for unlimited profit/loss
  const edgeProfit = calculateSpreadPayoff(maxPrice, legs);
  const edgeLoss = calculateSpreadPayoff(minPrice, legs);
  
  // If payoff is still increasing at the edge, it's likely unlimited
  const isUnlimitedProfit = edgeProfit === maxProfit && maxProfit > 0;
  const isUnlimitedLoss = edgeLoss === maxLoss && maxLoss < 0;
  
  return {
    maxProfit,
    maxLoss,
    maxProfitPrice,
    maxLossPrice,
    isUnlimitedProfit,
    isUnlimitedLoss
  };
};

// Generate chart data points
const generateChartData = (legs, currentPrice, priceRange = 0.5) => {
  const strikes = legs.map(l => l.strike_price || l.strike || currentPrice);
  const minStrike = Math.min(...strikes);
  const maxStrike = Math.max(...strikes);
  
  // Expand range around strikes and current price
  const rangeCenter = (minStrike + maxStrike) / 2;
  const rangeWidth = Math.max(maxStrike - minStrike, currentPrice * priceRange);
  
  const minPrice = Math.max(0.01, rangeCenter - rangeWidth * 1.5);
  const maxPrice = rangeCenter + rangeWidth * 1.5;
  
  const dataPoints = [];
  const steps = 200;
  const priceStep = (maxPrice - minPrice) / steps;
  
  for (let i = 0; i <= steps; i++) {
    const price = minPrice + (i * priceStep);
    const payoff = calculateSpreadPayoff(price, legs);
    dataPoints.push({ price, payoff });
  }
  
  return { dataPoints, minPrice, maxPrice };
};

// SVG Chart Component
const PayoffChart = ({ dataPoints, currentPrice, breakevens, minPrice, maxPrice, maxProfit, maxLoss }) => {
  const width = 600;
  const height = 300;
  const padding = { top: 20, right: 30, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Find min/max payoff for scaling
  const payoffs = dataPoints.map(d => d.payoff);
  const minPayoff = Math.min(...payoffs, 0);
  const maxPayoff = Math.max(...payoffs, 0);
  const payoffRange = maxPayoff - minPayoff || 100;
  const payoffPadding = payoffRange * 0.1;
  
  // Scale functions
  const xScale = (price) => padding.left + ((price - minPrice) / (maxPrice - minPrice)) * chartWidth;
  const yScale = (payoff) => padding.top + chartHeight - ((payoff - (minPayoff - payoffPadding)) / (payoffRange + payoffPadding * 2)) * chartHeight;
  
  // Generate path
  const pathData = dataPoints.map((d, i) => {
    const x = xScale(d.price);
    const y = yScale(d.payoff);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  
  // Generate filled areas (profit in green, loss in red)
  const zeroY = yScale(0);
  
  // Split path into profit and loss sections for coloring
  const profitPath = [];
  const lossPath = [];
  
  dataPoints.forEach((d, i) => {
    const x = xScale(d.price);
    const y = yScale(d.payoff);
    
    if (d.payoff >= 0) {
      profitPath.push({ x, y, payoff: d.payoff });
    } else {
      lossPath.push({ x, y, payoff: d.payoff });
    }
  });
  
  // Generate filled area paths
  const generateFilledPath = (points, fillToY) => {
    if (points.length < 2) return '';
    
    let path = `M ${points[0].x} ${fillToY}`;
    points.forEach(p => {
      path += ` L ${p.x} ${p.y}`;
    });
    path += ` L ${points[points.length - 1].x} ${fillToY} Z`;
    return path;
  };
  
  // Y-axis labels
  const yTicks = 5;
  const yTickValues = [];
  for (let i = 0; i <= yTicks; i++) {
    const value = (minPayoff - payoffPadding) + ((payoffRange + payoffPadding * 2) * i / yTicks);
    yTickValues.push(value);
  }
  
  return (
    <svg width={width} height={height} className="w-full h-auto">
      <defs>
        <linearGradient id="profitGradient" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="rgba(34, 197, 94, 0.1)" />
          <stop offset="100%" stopColor="rgba(34, 197, 94, 0.4)" />
        </linearGradient>
        <linearGradient id="lossGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(239, 68, 68, 0.1)" />
          <stop offset="100%" stopColor="rgba(239, 68, 68, 0.4)" />
        </linearGradient>
      </defs>
      
      {/* Background */}
      <rect x={padding.left} y={padding.top} width={chartWidth} height={chartHeight} fill="#f8fafc" rx="4" />
      
      {/* Zero line */}
      <line
        x1={padding.left}
        y1={zeroY}
        x2={width - padding.right}
        y2={zeroY}
        stroke="#94a3b8"
        strokeWidth="1"
        strokeDasharray="4,4"
      />
      
      {/* Fill areas */}
      {profitPath.length > 1 && (
        <path
          d={generateFilledPath(profitPath, zeroY)}
          fill="url(#profitGradient)"
        />
      )}
      {lossPath.length > 1 && (
        <path
          d={generateFilledPath(lossPath, zeroY)}
          fill="url(#lossGradient)"
        />
      )}
      
      {/* Main payoff line */}
      <path
        d={pathData}
        fill="none"
        stroke="url(#lineGradient)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          {dataPoints.map((d, i) => (
            <stop
              key={i}
              offset={`${(i / dataPoints.length) * 100}%`}
              stopColor={d.payoff >= 0 ? '#22c55e' : '#ef4444'}
            />
          ))}
        </linearGradient>
      </defs>
      
      {/* Current price line */}
      {currentPrice >= minPrice && currentPrice <= maxPrice && (
        <>
          <line
            x1={xScale(currentPrice)}
            y1={padding.top}
            x2={xScale(currentPrice)}
            y2={height - padding.bottom}
            stroke="#6366f1"
            strokeWidth="2"
            strokeDasharray="6,3"
          />
          <text
            x={xScale(currentPrice)}
            y={height - padding.bottom + 15}
            textAnchor="middle"
            fontSize="11"
            fill="#6366f1"
            fontWeight="bold"
          >
            ${currentPrice.toFixed(2)}
          </text>
          <text
            x={xScale(currentPrice)}
            y={height - padding.bottom + 28}
            textAnchor="middle"
            fontSize="9"
            fill="#6366f1"
          >
            Current
          </text>
        </>
      )}
      
      {/* Breakeven points */}
      {breakevens.map((be, idx) => (
        <g key={idx}>
          <circle
            cx={xScale(be)}
            cy={zeroY}
            r="6"
            fill="#000"
            stroke="#fff"
            strokeWidth="2"
          />
          <text
            x={xScale(be)}
            y={zeroY - 12}
            textAnchor="middle"
            fontSize="10"
            fill="#000"
            fontWeight="bold"
          >
            BE: ${be.toFixed(2)}
          </text>
        </g>
      ))}
      
      {/* Y-axis labels */}
      {yTickValues.map((val, i) => (
        <g key={i}>
          <line
            x1={padding.left - 5}
            y1={yScale(val)}
            x2={padding.left}
            y2={yScale(val)}
            stroke="#94a3b8"
          />
          <text
            x={padding.left - 10}
            y={yScale(val) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#64748b"
          >
            ${val.toFixed(0)}
          </text>
        </g>
      ))}
      
      {/* Axis labels */}
      <text
        x={padding.left - 45}
        y={height / 2}
        textAnchor="middle"
        fontSize="11"
        fill="#64748b"
        transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`}
      >
        Profit / Loss ($)
      </text>
      <text
        x={width / 2}
        y={height - 5}
        textAnchor="middle"
        fontSize="11"
        fill="#64748b"
      >
        Stock Price at Expiration ($)
      </text>
      
      {/* +/- indicators */}
      <text x={padding.left + 5} y={padding.top + 15} fontSize="12" fill="#22c55e" fontWeight="bold">+</text>
      <text x={padding.left + 5} y={height - padding.bottom - 10} fontSize="12" fill="#ef4444" fontWeight="bold">−</text>
    </svg>
  );
};

// Main Payoff Diagram Modal Component
export const PayoffDiagramModal = ({ isOpen, onClose, position, currentStockPrice }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Prevent modal from closing on internal clicks
  const handleContentClick = (e) => {
    e.stopPropagation();
  };
  
  // Normalize position data to legs array
  const legs = useMemo(() => {
    if (!position) return [];
    
    // If it's a spread with legs
    if (position.legs && Array.isArray(position.legs)) {
      return position.legs;
    }
    
    // Single position
    return [{
      symbol: position.symbol || position.underlying_symbol,
      strike_price: position.strike_price || position.strike,
      option_type: position.option_type,
      quantity: position.quantity,
      entry_premium: position.entry_premium || position.premium || position.average_open_price || 0,
      expiration_date: position.expiration_date
    }];
  }, [position]);
  
  // Get current price
  const stockPrice = currentStockPrice || position?.current_stock_price || position?.underlying_price || 100;
  
  // Calculate chart data
  const { chartData, breakevens, maxProfitLoss, minPrice, maxPrice } = useMemo(() => {
    if (legs.length === 0) return { chartData: [], breakevens: [], maxProfitLoss: null };
    
    const { dataPoints, minPrice, maxPrice } = generateChartData(legs, stockPrice);
    const be = findBreakevenPoints(legs, minPrice, maxPrice);
    const mpl = findMaxProfitLoss(legs, minPrice, maxPrice);
    
    return {
      chartData: dataPoints,
      breakevens: be,
      maxProfitLoss: mpl,
      minPrice,
      maxPrice
    };
  }, [legs, stockPrice]);
  
  // Calculate current P&L
  const currentPnL = useMemo(() => {
    if (legs.length === 0) return 0;
    return calculateSpreadPayoff(stockPrice, legs);
  }, [legs, stockPrice]);
  
  if (!isOpen || !position) return null;
  
  // Get position info
  const underlying = position.underlying_symbol || position.symbol?.split(' ')[0] || 'N/A';
  const strategyType = position.strategy_type || (legs.length > 1 ? 'Spread' : position.option_type);
  const expiration = position.expiration_date;
  
  // Handle close only from explicit close button
  const handleOpenChange = (open) => {
    // Only close from the explicit close button, not from overlay/escape
    // Do nothing here - we handle closing via the close button
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange} modal={true}>
      <DialogContent 
        className="max-w-3xl p-0 overflow-hidden [&>button]:hidden" 
        data-testid="payoff-diagram-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onClick={handleContentClick}
      >
        <DialogHeader className="px-6 py-4 bg-gradient-to-r from-slate-900 to-indigo-900">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-400" />
                Profit & Breakeven Chart
              </DialogTitle>
              <p className="text-sm text-slate-300 mt-1">
                {underlying} {strategyType} {expiration && `• Exp: ${expiration}`}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white/70 hover:text-white">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="p-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {/* Max Profit */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Max Profit</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {maxProfitLoss?.isUnlimitedProfit ? (
                  'Unlimited'
                ) : (
                  `$${maxProfitLoss?.maxProfit?.toFixed(2) || '0.00'}`
                )}
              </div>
              {!maxProfitLoss?.isUnlimitedProfit && maxProfitLoss?.maxProfitPrice && (
                <div className="text-xs text-green-600 mt-1">
                  at ${maxProfitLoss.maxProfitPrice.toFixed(2)}
                </div>
              )}
            </div>
            
            {/* Breakeven */}
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center gap-2 text-slate-700 mb-1">
                <Target className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Breakeven</span>
              </div>
              <div className="text-2xl font-bold text-slate-800">
                {breakevens.length > 0 ? (
                  breakevens.length === 1 ? (
                    `$${breakevens[0].toFixed(2)}`
                  ) : (
                    <span className="text-lg">
                      ${breakevens[0].toFixed(2)} / ${breakevens[1].toFixed(2)}
                    </span>
                  )
                ) : (
                  'N/A'
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Current: ${stockPrice.toFixed(2)}
              </div>
            </div>
            
            {/* Max Loss */}
            <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <TrendingDown className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Max Loss</span>
              </div>
              <div className="text-2xl font-bold text-red-700">
                {maxProfitLoss?.isUnlimitedLoss ? (
                  'Unlimited'
                ) : (
                  `$${Math.abs(maxProfitLoss?.maxLoss || 0).toFixed(2)}`
                )}
              </div>
              {!maxProfitLoss?.isUnlimitedLoss && maxProfitLoss?.maxLossPrice && (
                <div className="text-xs text-red-600 mt-1">
                  at ${maxProfitLoss.maxLossPrice.toFixed(2)}
                </div>
              )}
            </div>
          </div>
          
          {/* Current P&L */}
          <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
            currentPnL >= 0 ? 'bg-green-100 border border-green-300' : 'bg-red-100 border border-red-300'
          }`}>
            <div className="flex items-center gap-2">
              <DollarSign className={`h-5 w-5 ${currentPnL >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              <span className="font-medium text-gray-700">Current P&L at ${stockPrice.toFixed(2)}:</span>
            </div>
            <span className={`text-xl font-bold ${currentPnL >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {currentPnL >= 0 ? '+' : ''}{currentPnL.toFixed(2)}
            </span>
          </div>
          
          {/* Chart */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            {chartData.length > 0 ? (
              <PayoffChart
                dataPoints={chartData}
                currentPrice={stockPrice}
                breakevens={breakevens}
                minPrice={minPrice}
                maxPrice={maxPrice}
                maxProfit={maxProfitLoss?.maxProfit}
                maxLoss={maxProfitLoss?.maxLoss}
              />
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400">
                <p>Unable to generate payoff chart</p>
              </div>
            )}
          </div>
          
          {/* Position Details Accordion */}
          <div className="mt-4 border rounded-lg overflow-hidden">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <span className="font-medium text-slate-700 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Position Details
              </span>
              {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            {showDetails && (
              <div className="p-4 bg-white">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[460px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-gray-500">Type</th>
                      <th className="text-left py-2 text-gray-500">Strike</th>
                      <th className="text-left py-2 text-gray-500">Qty</th>
                      <th className="text-left py-2 text-gray-500">Premium</th>
                      <th className="text-left py-2 text-gray-500">Expiration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legs.map((leg, idx) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-2">
                          <Badge className={leg.option_type?.toLowerCase() === 'call' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {leg.quantity > 0 ? 'Long' : 'Short'} {leg.option_type}
                          </Badge>
                        </td>
                        <td className="py-2 font-medium">${leg.strike_price || leg.strike || 'N/A'}</td>
                        <td className="py-2">{Math.abs(leg.quantity)}</td>
                        <td className="py-2">${(leg.entry_premium || leg.premium || 0).toFixed(2)}</td>
                        <td className="py-2 text-gray-500">{leg.expiration_date || position.expiration_date || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
          
          {/* Disclaimer */}
          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              This chart shows theoretical profit/loss at expiration. Actual results may vary due to factors 
              including time decay, implied volatility changes, and early exercise. For educational purposes only.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Floating button to open payoff diagram - uses context for stable modal
export const PayoffDiagramButton = ({ position, currentPrice, size = 'sm' }) => {
  // Try to use context if available, fall back to local state
  const [localIsOpen, setLocalIsOpen] = useState(false);
  
  // Stable close handler
  const handleClose = useCallback(() => {
    setLocalIsOpen(false);
  }, []);
  
  // Stable open handler
  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setLocalIsOpen(true);
  }, []);
  
  if (!position) return null;
  
  return (
    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <Button
        variant="outline"
        size={size}
        onClick={handleOpen}
        className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700 hover:from-amber-100 hover:to-orange-100"
        title="View Profit & Breakeven Chart"
        data-testid="payoff-diagram-btn"
      >
        <Target className="h-4 w-4 mr-1" />
        P&L Chart
      </Button>
      
      {localIsOpen && (
        <PayoffDiagramModal
          isOpen={localIsOpen}
          onClose={handleClose}
          position={position}
          currentStockPrice={currentPrice}
        />
      )}
    </div>
  );
};

export default PayoffDiagramModal;
